/**
 * @fileoverview Input/output schemas for story-feedback-v1.
 *
 * This agent reads the full written story and produces multi-perspective
 * editorial feedback from three distinct personas ("hats"):
 *  - A yellow press critic writing a punchy, no-holds-barred roast
 *  - A developmental editor offering craft-focused, constructive notes
 *  - A publisher weighing commercial and audience fit
 *
 * Input is deliberately minimal: just the concatenated story prose. No
 * plan, storyline, or snapshot data is provided — the agent is meant to
 * react to the actual writing as a reader/critic would.
 *
 * Output is flat (3 levels max) for Gemini API compatibility.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.fullStoryContent.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * A single hat's feedback. Each hat shares the same shape so the UI can
 * render them uniformly while the prose voice differs dramatically.
 */
const HatFeedbackSchema = z.object({
  headline: z.string().describe(
    "A one-line hook summarizing this hat's take on the story. "
    + 'Voice must match the persona (tabloid headline, editorial note, pitch line).'
  ),
  rating: z.number().min(1).max(10).describe(
    'An overall rating from 1 to 10 from this persona. 1 = unsalvageable, 10 = exceptional.'
  ),
  body: z.string().describe(
    'The full critique in this persona\'s voice. 3-6 paragraphs of Markdown. '
    + 'Use concrete references to the story — characters, scenes, moments — not generic advice.'
  ),
  strengths: z.array(z.string()).describe(
    'Short bullet points (1 sentence each) of what this hat thinks works. Empty array if nothing lands.'
  ),
  concerns: z.array(z.string()).describe(
    'Short bullet points (1 sentence each) of what this hat thinks is broken, weak, or risky.'
  ),
});

export type HatFeedback = z.infer<typeof HatFeedbackSchema>;

export const OutputSchema = z.object({
  overallImpression: z.string().describe(
    'A 2-3 sentence neutral summary of the story, written in a normal editorial voice (not in any of the three personas). '
    + 'This sits above the hats to ground the reader before the opinionated takes.'
  ),
  yellowPressCritic: HatFeedbackSchema.describe(
    'A tabloid / yellow-press critic\'s roast. Mean, punchy, quotable. '
    + 'Not cruel for its own sake — still grounded in the actual text — but unfiltered.'
  ),
  developmentalEditor: HatFeedbackSchema.describe(
    'A developmental editor\'s feedback. Craft-first: structure, character arcs, pacing, theme, stakes. '
    + 'Constructive and specific. Calls out weaknesses without sugarcoating, and flags what\'s working so the author knows what to protect.'
  ),
  publisher: HatFeedbackSchema.describe(
    'A publisher\'s read on audience fit and market viability. Who is this for? '
    + 'What would a cover pitch look like? What will click with readers and what might repel them? '
    + 'Commercial but not cynical.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
